import { useState, useEffect, useRef, useCallback } from "react";
import api from "../../api/client";

/**
 * TeamNumberSelect — dropdown for selecting a team from TeamMaster.
 *
 * Props:
 *   value        — selected team_master_id (number | null)
 *   onChange      — (teamMasterId: number | null, teamData: object | null) => void
 *   companyId     — company to filter teams by (optional, defaults to user's company)
 *   assignmentDate — date string "YYYY-MM-DD" for same-day composition lookup
 *   disabled      — boolean
 *   onCompositionLoaded — (composition: object | null) => void
 *   className     — additional CSS classes
 */
export default function TeamNumberSelect({
  value,
  onChange,
  companyId,
  assignmentDate,
  disabled = false,
  onCompositionLoaded,
  className = "",
}) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const prevCompId = useRef(companyId);

  // Fetch active teams (company-scoped)
  const fetchTeams = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      const { data } = await api.get("/team-masters/active", { params });
      setTeams(data);
    } catch {
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // Re-fetch if companyId changes
  useEffect(() => {
    if (prevCompId.current !== companyId) {
      prevCompId.current = companyId;
      fetchTeams();
    }
  }, [companyId, fetchTeams]);

  // When value or assignmentDate changes, fetch composition
  useEffect(() => {
    if (!value || !assignmentDate || !onCompositionLoaded) return;
    let cancelled = false;
    api
      .get("/team-masters/composition", {
        params: { team_master_id: value, assignment_date: assignmentDate },
      })
      .then(({ data }) => {
        if (!cancelled) onCompositionLoaded(data);
      })
      .catch(() => {
        if (!cancelled) onCompositionLoaded(null);
      });
    return () => { cancelled = true; };
  }, [value, assignmentDate]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedTeam = teams.find((t) => t.id === value);
  const searchTerm = search.trim().toLowerCase();
  const filtered = searchTerm
    ? teams.filter((t) => {
        const teamNumber = (t.team_number || "").toLowerCase();
        const personnel1 = (t.default_personnel_1_name || "").toLowerCase();
        const personnel2 = (t.default_personnel_2_name || "").toLowerCase();
        return (
          teamNumber.includes(searchTerm) ||
          personnel1.includes(searchTerm) ||
          personnel2.includes(searchTerm)
        );
      })
    : teams;

  const handleSelect = (team) => {
    setOpen(false);
    setSearch("");
    onChange(team.id, team);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null, null);
    if (onCompositionLoaded) onCompositionLoaded(null);
  };

  return (
    <div className={`relative ${className}`} ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full border rounded px-3 py-2 text-sm text-left flex items-center justify-between gap-2 ${
          disabled
            ? "bg-gray-50 text-gray-400 cursor-not-allowed"
            : "bg-white hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
        }`}
      >
        <span className={selectedTeam ? "text-gray-900" : "text-gray-400"}>
          {loading ? "Loading..." : selectedTeam ? selectedTeam.team_number : "— Select Team —"}
        </span>
        <span className="flex items-center gap-1">
          {selectedTeam && !disabled && (
            <span
              onClick={handleClear}
              className="text-gray-400 hover:text-red-500 cursor-pointer"
              title="Clear"
            >
              ×
            </span>
          )}
          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search team number..."
              className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-sm text-gray-400 text-center">
                {loading ? "Loading teams..." : "No teams found"}
              </div>
            ) : (
              filtered.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => handleSelect(t)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 flex items-center justify-between ${
                    t.id === value ? "bg-blue-50 font-medium" : ""
                  }`}
                >
                  <span>
                    <span className="font-medium">{t.team_number}</span>
                    <span className="text-gray-400 ml-2 text-xs">
                      {t.default_personnel_1_name}
                      {t.default_personnel_2_name ? `, ${t.default_personnel_2_name}` : ""}
                    </span>
                  </span>
                  {t.id === value && (
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
